// frontend/src/pages/ExclusionsPage.tsx - UPDATED FOR HOOKS/STORE INTEGRATION
// MODIFIED FUNCTION: Updated to use new API hooks, store, and utils following the pattern of NotificationsPage and AttachmentsPage

import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, truncateText } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// FIXED: Use entity-specific hooks following the pattern
import {
  useExclusions,
  useCreateExclusion,
  useUpdateExclusion,
  useDeleteExclusion,
  useTestExclusion,
  useExclusionValidation
} from '@/hooks/useExclusions';

// FIXED: Import useOrganizations for organization list
import { useOrganizations } from '@/hooks/useOrganizations';

// FIXED: Import API types
import type {
  Exclusion,
  ExclusionQueryParams,
  ExclusionCreateRequest,
  ExclusionUpdateRequest
} from '@/api/types';

// ===== UTILITY FUNCTIONS =====

// Debounced value hook
function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ===== ERROR BOUNDARY =====

class ExclusionsErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('ExclusionsPage Error Boundary:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ExclusionsPage Error:', error, errorInfo);
    
    // Report error to app store for global error handling
    const { setGlobalError } = useAppStore.getState();
    setGlobalError(`Exclusions Page Error: ${error.message}`);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Something went wrong
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <button
                className="mt-2 text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
                onClick={() => window.location.reload()}
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== INTERFACES =====

interface ExclusionStats {
  totalExclusions: number;
  activeExclusions: number;
  inactiveExclusions: number;
  byType: Record<string, number>;
  byScope: {
    organization: number;
    user: number;
  };
  matchStatistics: {
    totalMatches: number;
    matchesToday: number;
    averageMatchesPerDay: number;
  };
  performanceMetrics: {
    averageProcessingTime: number;
    fastestRule: string;
    slowestRule: string;
  };
}

interface BulkOperationRequest {
  action: 'activate' | 'deactivate' | 'delete' | 'update';
  exclusionIds: string[];
  data?: Partial<ExclusionUpdateRequest>;
  reason?: string;
}

// CORRECTED: Backend-matching exclusion structure
interface ExclusionWithConditions extends Exclusion {
  conditions: {
    senderDomain?: string[];
    sender?: string[];
    subject?: string[];
    body?: string[];
    attachmentName?: string[];
    attachmentContent?: string[];
  };
  scope: 'organization' | 'user';
}

// ===== EXCLUSION CARD COMPONENT =====
const ExclusionCard = memo<{
  exclusion: ExclusionWithConditions;
  onEdit: (exclusion: ExclusionWithConditions) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string, isActive: boolean) => void;
  onTest: (exclusion: ExclusionWithConditions) => void;
}>(({ exclusion, onEdit, onDelete, onToggleStatus, onTest }) => {
  
  const [isExpanded, setIsExpanded] = useState(false);

  const getScopeColor = useCallback((scope: string): string => {
    switch (scope) {
      case 'organization': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'user': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  }, []);

  const getConditionsCount = useCallback((conditions: ExclusionWithConditions['conditions']): number => {
    return Object.values(conditions).reduce((count, arr) => count + (arr?.length || 0), 0);
  }, []);

  const exclusionId = (exclusion as any)._id || exclusion.id || '';

  // Event handlers
  const handleEdit = useCallback(() => onEdit(exclusion), [onEdit, exclusion]);
  const handleDelete = useCallback(() => onDelete(exclusionId), [onDelete, exclusionId]);
  const handleToggleStatus = useCallback(() => onToggleStatus(exclusionId, !exclusion.isActive), [onToggleStatus, exclusionId, exclusion.isActive]);
  const handleTest = useCallback(() => onTest(exclusion), [onTest, exclusion]);

  return (
    <div className={cn(
      "bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-4 transition-all duration-200 hover:shadow-md",
      exclusion.isActive 
        ? "border-gray-200 dark:border-gray-700" 
        : "border-gray-300 dark:border-gray-600 opacity-60"
    )}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {exclusion.name}
            </h3>
            <span className={cn(
              'px-2 py-1 text-xs font-medium rounded-full',
              getScopeColor(exclusion.scope)
            )}>
              {exclusion.scope}
            </span>
            <span className={cn(
              'px-2 py-1 text-xs font-medium rounded-full',
              exclusion.isActive 
                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
            )}>
              {exclusion.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
            <span>{getConditionsCount(exclusion.conditions)} conditions</span>
            <span>•</span>
            <span>{formatDate(exclusion.createdAt)}</span>
            {exclusion.updatedAt && exclusion.updatedAt !== exclusion.createdAt && (
              <>
                <span>•</span>
                <span>Updated {formatDate(exclusion.updatedAt)}</span>
              </>
            )}
          </div>
          {exclusion.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              {truncateText(exclusion.description, 100)}
            </p>
          )}
        </div>

        {/* Action Menu */}
        <div className="flex items-center space-x-1 ml-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleStatus}
            className={cn(
              "text-xs",
              exclusion.isActive
                ? "text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300"
                : "text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300"
            )}
            title={exclusion.isActive ? 'Deactivate' : 'Activate'}
          >
            {exclusion.isActive ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleTest}
            className="text-xs"
            title="Test Rule"
          >
            Test
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs"
            title="Toggle Details"
          >
            {isExpanded ? '−' : '+'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleEdit}
            className="text-xs"
            title="Edit"
          >
            ✏
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            title="Delete"
          >
            🗑
          </Button>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-4 space-y-4 border-t border-gray-200 dark:border-gray-600 pt-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Conditions</h4>
            <div className="space-y-2">
              {Object.entries(exclusion.conditions).map(([key, values]) => {
                if (!values || values.length === 0) return null;
                return (
                  <div key={key} className="flex items-start space-x-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 capitalize min-w-0 flex-shrink-0">
                      {key.replace(/([A-Z])/g, ' $1').trim()}:
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {values.map((value, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded"
                        >
                          {truncateText(value, 30)}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {exclusion.description && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {exclusion.description}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 dark:text-gray-400">
            <div>
              <span className="font-medium">Created:</span> {formatDate(exclusion.createdAt)}
            </div>
            <div>
              <span className="font-medium">Updated:</span> {formatDate(exclusion.updatedAt)}
            </div>
            <div>
              <span className="font-medium">Created By:</span> {exclusion.createdBy || 'Unknown'}
            </div>
            <div>
              <span className="font-medium">Organization:</span> {exclusion.organization}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ExclusionCard.displayName = 'ExclusionCard';

// ===== CREATE EXCLUSION MODAL =====
const CreateExclusionModal = memo<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedOrganization?: string;
  organizations?: any[];
  currentUser?: any;
}>(({ isOpen, onClose, onSuccess, selectedOrganization, organizations = [], currentUser }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ruleType: 'exclude' as 'exclude' | 'include',
    scope: 'organization' as 'organization' | 'user',
    conditions: {
      senderDomain: [] as string[],
      sender: [] as string[],
      subject: [] as string[],
      body: [] as string[],
      attachmentName: [] as string[],
      attachmentContent: [] as string[]
    },
    priority: 0,
    isActive: true
  });
  
  const [newConditionField, setNewConditionField] = useState<string>('sender');
  const [newConditionValue, setNewConditionValue] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { setGlobalError, clearGlobalError } = useAppStore();
  
  const createMutation = useCreateExclusion({
    onSuccess: () => {
      onSuccess();
      onClose();
      resetForm();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Failed to create exclusion: ${error.message || error}`);
    }
  });

  const { validateExclusionData } = useExclusionValidation();

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      ruleType: 'exclude',
      scope: 'organization',
      conditions: {
        senderDomain: [],
        sender: [],
        subject: [],
        body: [],
        attachmentName: [],
        attachmentContent: []
      },
      priority: 0,
      isActive: true
    });
    setNewConditionField('sender');
    setNewConditionValue('');
    setErrors({});
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleAddCondition = useCallback(() => {
    if (!newConditionValue.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [newConditionField]: [
          ...(prev.conditions[newConditionField as keyof typeof prev.conditions] || []),
          newConditionValue.trim()
        ]
      }
    }));
    setNewConditionValue('');
  }, [newConditionField, newConditionValue]);

  const handleRemoveCondition = useCallback((field: string, index: number) => {
    setFormData(prev => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [field]: (prev.conditions[field as keyof typeof prev.conditions] || []).filter((_, i) => i !== index)
      }
    }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateExclusionData(formData);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    // Validate organization is selected
    if (!selectedOrganization) {
      setGlobalError('Please select an organization before creating a rule');
      return;
    }

    try {
      // Transform formData to match ExclusionCreateRequest interface
      // Use 'sender' as the default type since we're handling multiple condition types
      const createRequest: ExclusionCreateRequest = {
        type: 'sender', // Use a valid type from the union
        pattern: '', // Not used for conditions-based exclusions
        // Spread all formData properties (includes name, description, isActive, scope, conditions)
        ...formData,
        // Add organization ID for implementors
        organizationId: selectedOrganization
      };
      createMutation.mutate(createRequest);
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [formData, validateExclusionData, createMutation, selectedOrganization, setGlobalError]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Create Exclusion Rule
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Create a new exclusion rule to filter emails before project classification.
                  </p>
                </div>
                
                <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                  {/* Organization Display */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Organization
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white font-medium">
                      {selectedOrganization
                        ? (organizations.find((org: any) => (org._id || org.id) === selectedOrganization)?.name || 'Unknown Organization')
                        : 'No organization selected'}
                    </p>
                    {!selectedOrganization && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        Please select an organization before creating a rule
                      </p>
                    )}
                  </div>

                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Rule Name *
                    </label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className={errors.name ? 'border-red-300' : ''}
                      placeholder="e.g., Spam Emails or Important Clients"
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                  </div>

                  {/* Rule Type */}
                  <div>
                    <label htmlFor="ruleType" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Rule Type *
                    </label>
                    <select
                      id="ruleType"
                      value={formData.ruleType}
                      onChange={(e) => setFormData(prev => ({ ...prev, ruleType: e.target.value as 'exclude' | 'include' }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="exclude">Exclusion - Prevent classification</option>
                      <option value="include">Inclusion - Force classification</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {formData.ruleType === 'exclude'
                        ? 'Emails matching this rule will NOT be classified as correspondences'
                        : 'Emails matching this rule will ALWAYS be classified, even with low confidence'}
                    </p>
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description
                    </label>
                    <Input
                      id="description"
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of what this rule does"
                    />
                  </div>

                  {/* Priority */}
                  <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Priority (0-100)
                    </label>
                    <Input
                      id="priority"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Higher priority rules are evaluated first (default: 0)
                    </p>
                  </div>

                  {/* Scope */}
                  <div>
                    <label htmlFor="scope" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Scope
                    </label>
                    <select
                      id="scope"
                      value={formData.scope}
                      onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value as 'organization' | 'user' }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="organization">Organization</option>
                      <option value="user">User</option>
                    </select>
                  </div>

                  {/* Add Condition */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Add Conditions *
                    </label>
                    <div className="flex space-x-2">
                      <select
                        value={newConditionField}
                        onChange={(e) => setNewConditionField(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="sender">Sender</option>
                        <option value="senderDomain">Sender Domain</option>
                        <option value="subject">Subject</option>
                        <option value="body">Body</option>
                        <option value="attachmentName">Attachment Name</option>
                        <option value="attachmentContent">Attachment Content</option>
                      </select>
                      <Input
                        type="text"
                        value={newConditionValue}
                        onChange={(e) => setNewConditionValue(e.target.value)}
                        placeholder="Enter condition value"
                        className="flex-1"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCondition();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        onClick={handleAddCondition}
                        disabled={!newConditionValue.trim()}
                        variant="secondary"
                      >
                        Add
                      </Button>
                    </div>
                    {errors.conditions && <p className="mt-1 text-sm text-red-600">{errors.conditions}</p>}
                  </div>

                  {/* Conditions List */}
                  <div className="space-y-2">
                    {Object.entries(formData.conditions).map(([key, values]) => {
                      if (!values || values.length === 0) return null;
                      return (
                        <div key={key} className="space-y-1">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()} ({values.length})
                          </h4>
                          <div className="flex flex-wrap gap-1">
                            {values.map((value, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 rounded-full"
                              >
                                {value}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveCondition(key, index)}
                                  className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Active Status */}
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                    </label>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={createMutation.loading}
              className="w-full sm:w-auto sm:ml-3"
            >
              {createMutation.loading ? <LoadingSpinner size="sm" /> : 'Create Rule'}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="secondary"
              className="mt-3 w-full sm:mt-0 sm:w-auto"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

CreateExclusionModal.displayName = 'CreateExclusionModal';

// ===== MAIN EXCLUSIONS PAGE COMPONENT =====
export const ExclusionsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'organization' | 'user'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedOrganization, setSelectedOrganization] = useState<string>(
    searchParams.get('organization') || ''
  );
  const [selectedExclusions, setSelectedExclusions] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExclusion, setEditingExclusion] = useState<ExclusionWithConditions | null>(null);

  const { getSafeUser } = useAuthStore();
  const { setGlobalError, clearGlobalError } = useAppStore();
  const { confirm } = useConfirmation();
  const currentUser = getSafeUser();

  // Debounced search term
  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  // Load organizations for implementors using the proper hook
  const { data: organizationsResponse } = useOrganizations();
  const organizations = organizationsResponse?.data || [];

  // For admins, set their organization automatically
  // For implementors, require explicit selection (don't auto-select)
  useEffect(() => {
    if (currentUser?.role === 'admin' && currentUser?.organization) {
      const orgId = typeof currentUser.organization === 'string'
        ? currentUser.organization
        : (currentUser.organization as any)?._id || (currentUser.organization as any)?.id;
      setSelectedOrganization(orgId);
    }
  }, [currentUser]);

  // Query params following pattern from other pages
  const queryParams = useMemo((): ExclusionQueryParams => {
    const params: ExclusionQueryParams = {
      page: currentPage,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc'
    };

    if (debouncedSearchTerm?.trim()) {
      params.search = debouncedSearchTerm.trim();
    }

    if (scopeFilter !== 'all') {
      (params as any).scope = scopeFilter;
    }

    if (statusFilter !== 'all') {
      (params as any).isActive = statusFilter === 'active';
    }

    // Add organization filter for implementors
    if (selectedOrganization && currentUser?.role === 'implementor') {
      (params as any).organizationId = selectedOrganization;
    }

    return params;
  }, [debouncedSearchTerm, scopeFilter, statusFilter, currentPage, selectedOrganization, currentUser?.role]);

  // Load exclusions using new API pattern
  const {
    data: exclusionsResponse,
    loading: exclusionsLoading,
    error: exclusionsError,
    refetch: refreshExclusions
  } = useExclusions(queryParams);

  // Don't load analytics - it's not implemented yet in backend
  // const {
  //   data: statsResponse,
  //   refetch: refreshStats
  // } = useExclusionAnalytics();
  const statsResponse = null;
  const refreshStats = () => {};

  // Error handling
  useEffect(() => {
    if (exclusionsError) {
      setGlobalError(`Exclusions loading error: ${exclusionsError}`);
    } else {
      clearGlobalError();
    }
  }, [exclusionsError, setGlobalError, clearGlobalError]);

  // Mutation hooks
  const updateMutation = useUpdateExclusion({
    onSuccess: () => {
      refreshExclusions();
      refreshStats();
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Update failed: ${error.message || error}`);
    }
  });

  const deleteMutation = useDeleteExclusion({
    onSuccess: () => {
      refreshExclusions();
      refreshStats();
      setSelectedExclusions(prev => prev.filter(id => id !== prev[0])); // Remove the deleted one
      clearGlobalError();
    },
    onError: (error: any) => {
      setGlobalError(`Delete failed: ${error.message || error}`);
    }
  });

  const testMutation = useTestExclusion({
    onSuccess: (result) => {
      // Show test results in a modal or notification
    },
    onError: (error: any) => {
      setGlobalError(`Test failed: ${error.message || error}`);
    }
  });

  // Extract data from responses
  const exclusions = exclusionsResponse?.data || [];
  const pagination = exclusionsResponse?.pagination;
  const stats: ExclusionStats = statsResponse || {
    totalExclusions: 0,
    activeExclusions: 0,
    inactiveExclusions: 0,
    byType: {},
    byScope: {
      organization: 0,
      user: 0
    },
    matchStatistics: {
      totalMatches: 0,
      matchesToday: 0,
      averageMatchesPerDay: 0
    },
    performanceMetrics: {
      averageProcessingTime: 0,
      fastestRule: '',
      slowestRule: ''
    }
  };

  // Event handlers
  const handleEdit = useCallback((exclusion: ExclusionWithConditions) => {
    setEditingExclusion(exclusion);
    setShowEditModal(true);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Exclusion Rule',
      message: 'Are you sure you want to delete this exclusion rule? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        deleteMutation.mutate(id);
      } catch (error) {
        // Error handled by mutation onError
      }
    }
  }, [deleteMutation, confirm]);

  const handleToggleStatus = useCallback(async (id: string, isActive: boolean) => {
    try {
      updateMutation.mutate({ id, data: { isActive } });
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [updateMutation]);

  const handleTest = useCallback(async (exclusion: ExclusionWithConditions) => {
    // For testing, we'll create a simple test request
    // Since we don't know the exact structure of ExclusionTestRequest, we'll use a basic approach
    const exclusionId = (exclusion as any)._id || exclusion.id;
    
    try {
      // Use the mutation with just the exclusion ID - the hook will handle the request structure
      testMutation.mutate(exclusionId);
    } catch (error) {
      // Error handled by mutation onError
    }
  }, [testMutation]);

  const handleSelectAll = useCallback(() => {
    if (selectedExclusions.length === exclusions.length) {
      setSelectedExclusions([]);
    } else {
      setSelectedExclusions(exclusions.map(e => (e as any)._id || e.id || '').filter(Boolean));
    }
  }, [selectedExclusions.length, exclusions]);

  const handleSelectExclusion = useCallback((id: string) => {
    setSelectedExclusions(prev => 
      prev.includes(id) 
        ? prev.filter(exclusionId => exclusionId !== id)
        : [...prev, id]
    );
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setSelectedExclusions([]); // Clear selection when changing pages
  }, []);

  const handleCreateSuccess = useCallback(() => {
    refreshExclusions();
    refreshStats();
  }, [refreshExclusions, refreshStats]);

  // Page actions
  const actions = useMemo(() => {
    const actionButtons = [];

    // For implementors, require organization selection before allowing rule creation
    const canCreate = currentUser?.role === 'admin' ||
                     (currentUser?.role === 'implementor' && selectedOrganization);

    actionButtons.push(
      <Button
        key="create"
        onClick={() => setShowCreateModal(true)}
        variant="primary"
        disabled={!canCreate}
        title={!canCreate ? 'Please select an organization first' : 'Create a new exclusion rule'}
      >
        Create Rule
      </Button>
    );

    return actionButtons;
  }, [currentUser, selectedOrganization]);

  // Permission check
  const hasAccess = !!currentUser;

  if (!hasAccess) {
    return (
      <PageLayout title="Email Exclusions" subtitle="Access Restricted">
        <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Authentication Required
              </h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                <p>Please log in to view and manage exclusion rules.</p>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // If implementor hasn't selected organization, show only the selector
  if (currentUser?.role === 'implementor' && !selectedOrganization) {
    return (
      <ExclusionsErrorBoundary>
        <PageLayout
          title="Email Exclusions"
          subtitle="Manage rules to exclude emails from project classification"
        >
          <div className="max-w-2xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Select an Organization
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Choose which organization's exclusion rules you want to manage
                </p>
              </div>

              <div>
                <label htmlFor="organization-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Organization
                </label>
                <select
                  id="organization-select"
                  value={selectedOrganization}
                  onChange={(e) => setSelectedOrganization(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                >
                  <option value="">Select an organization...</option>
                  {organizations.map((org: any) => (
                    <option key={org._id || org.id} value={org._id || org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </PageLayout>
      </ExclusionsErrorBoundary>
    );
  }

  return (
    <ExclusionsErrorBoundary>
      <PageLayout
        title="Email Exclusions"
        subtitle={`Manage rules to exclude emails from project classification • ${stats.totalExclusions} rules • ${stats.activeExclusions} active`}
        actions={actions}
        loading={exclusionsLoading}
      >
        <div className="space-y-6">
          {/* Organization Selector for Implementors */}
          {currentUser?.role === 'implementor' && organizations.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <label htmlFor="organization-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organization
              </label>
              <select
                id="organization-select"
                value={selectedOrganization}
                onChange={(e) => setSelectedOrganization(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select an organization...</option>
                {organizations.map((org: any) => (
                  <option key={org._id || org.id} value={org._id || org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalExclusions}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Total Rules
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.activeExclusions}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Active Rules
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {stats.matchStatistics.totalMatches}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Total Matches
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {stats.matchStatistics.matchesToday}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Today's Matches
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Organization Selector (for implementors only) */}
              {currentUser?.role === 'implementor' && organizations.length > 0 && (
                <div className="md:col-span-4 mb-4">
                  <label htmlFor="organization-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Organization *
                  </label>
                  <select
                    id="organization-select"
                    value={selectedOrganization}
                    onChange={(e) => setSelectedOrganization(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Organization</option>
                    {organizations.map((org: any) => (
                      <option key={org._id || org.id} value={org._id || org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Select the organization to manage exclusion rules for
                  </p>
                </div>
              )}

              {/* Search */}
              <div className="md:col-span-2">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search Rules
                </label>
                <Input
                  id="search"
                  type="text"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Scope Filter */}
              <div>
                <label htmlFor="scope-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Scope
                </label>
                <select
                  id="scope-filter"
                  value={scopeFilter}
                  onChange={(e) => setScopeFilter(e.target.value as 'all' | 'organization' | 'user')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Scopes</option>
                  <option value="organization">Organization</option>
                  <option value="user">User</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Exclusions List */}
          {exclusionsLoading ? (
            <div className="text-center py-12">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-500 dark:text-gray-400">Loading exclusion rules...</p>
            </div>
          ) : exclusionsError ? (
            <ErrorAlert 
              message={exclusionsError}
              onClose={() => clearGlobalError()}
            />
          ) : exclusions.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {searchTerm || scopeFilter !== 'all' || statusFilter !== 'all'
                  ? 'No exclusion rules found' 
                  : 'No exclusion rules yet'
                }
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {searchTerm || scopeFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your search criteria.'
                  : 'Create your first exclusion rule to filter emails before project classification.'
                }
              </p>
              {!searchTerm && scopeFilter === 'all' && statusFilter === 'all' && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  variant="primary"
                >
                  Create First Rule
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Selection Header */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedExclusions.length === exclusions.length && exclusions.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {selectedExclusions.length > 0 
                          ? `${selectedExclusions.length} selected`
                          : 'Select all'
                        }
                      </span>
                    </label>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {exclusions.length} exclusion rule{exclusions.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Exclusions */}
              <div className="space-y-4">
                {exclusions.map((exclusion) => {
                  const exclusionId = (exclusion as any)._id || exclusion.id || '';
                  return (
                    <div key={exclusionId} className="relative">
                      <div className="absolute left-2 top-2 z-10">
                        <input
                          type="checkbox"
                          checked={selectedExclusions.includes(exclusionId)}
                          onChange={() => handleSelectExclusion(exclusionId)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                      <div className="pl-8">
                        <ExclusionCard
                          exclusion={exclusion as ExclusionWithConditions}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onToggleStatus={handleToggleStatus}
                          onTest={handleTest}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1}
                        variant="outline"
                        size="sm"
                      >
                        Previous
                      </Button>
                      <Button
                        onClick={() => handlePageChange(Math.min(pagination.totalPages, currentPage + 1))}
                        disabled={currentPage >= pagination.totalPages}
                        variant="outline"
                        size="sm"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Info Card */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  About Email Classification Rules
                </h3>
                <div className="mt-2 text-sm text-blue-700 dark:text-blue-300 space-y-2">
                  <p>
                    <strong>Exclusion Rules:</strong> Prevent specific emails from being automatically classified into projects.
                    These rules are processed first, allowing you to filter out unwanted emails like spam, newsletters, or system notifications.
                  </p>
                  <p>
                    <strong>Inclusion Rules:</strong> Force specific emails to be classified as correspondences even if they don't meet
                    normal classification criteria. Useful for important clients, VIP contacts, or critical email patterns that should never be missed.
                  </p>
                  <p className="text-xs">
                    <strong>Priority:</strong> Rules with higher priority (0-100) are evaluated first. Exclusion rules always run before inclusion rules.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <CreateExclusionModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          selectedOrganization={selectedOrganization}
          organizations={organizations}
          currentUser={currentUser}
        />
      </PageLayout>
    </ExclusionsErrorBoundary>
  );
};